import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { BuildAudience, Variable } from "@/types";
import { useAudienceActions } from "@/context/Audience";

/**
 * UI state shared by the build page's builder components. The audience itself
 * lives in AudienceContext and the tags in the query cache (useVariables).
 */
export interface BuilderState {
  /** The group variables are added to, or null when none is open */
  activeGroup: number | null;
  /** Tag ids in any group */
  selected: Set<number>;
  /** Tag ids in the active group */
  active: Set<number>;
  disabled: boolean;
}

export interface BuilderActions {
  setActiveGroup: (groupId: number | null) => void;
  /** Opens the group, or closes it if it's already open */
  toggleGroup: (groupId: number) => void;
  /** Adds a group and opens it */
  addGroup: () => void;
  /** Removes a group, keeping the right group open as the rest are renumbered */
  removeGroup: (groupId: number) => void;
  /** Adds variables to the active group */
  selectVariables: (variables: Variable[]) => void;
  /** Removes a variable from the active group */
  removeVariable: (variable: Variable) => void;
}

const BuilderStateContext = createContext<BuilderState | undefined>(undefined);
const BuilderActionsContext = createContext<BuilderActions | undefined>(undefined);

const tagIds = (variables: Variable[]) => variables.map((v) => v.tag_id);

export const BuilderProvider: React.FC<{
  audience: BuildAudience;
  disabled: boolean;
  children: React.ReactNode;
}> = ({ audience, disabled, children }) => {
  const audienceActions = useAudienceActions();
  const groups = audience.var_groups;

  // Start with the first group open for a new audience, so variables can be added straight away
  const [activeGroup, setActiveGroup] = useState<number | null>(() =>
    disabled || (groups[0]?.variables.length ?? 0) > 0 ? null : 1
  );

  // The latest values for the actions, which never change identity
  const latest = useRef({ activeGroup, groupCount: groups.length });
  useEffect(() => {
    latest.current = { activeGroup, groupCount: groups.length };
  }, [activeGroup, groups.length]);

  const toggleGroup = useCallback((groupId: number) =>
    setActiveGroup((current) => current === groupId ? null : groupId), []);

  // Groups are numbered 1..n, so the new one is n + 1
  const addGroup = useCallback(() => {
    audienceActions.addGroup();
    setActiveGroup(latest.current.groupCount + 1);
  }, [audienceActions]);

  const removeGroup = useCallback((groupId: number) => {
    audienceActions.removeGroup(groupId);
    setActiveGroup((current) => {
      if (current === groupId) return null;
      return current !== null && current > groupId ? current - 1 : current;
    });
  }, [audienceActions]);

  const selectVariables = useCallback((variables: Variable[]) => {
    const groupId = latest.current.activeGroup;
    if (groupId !== null) audienceActions.addVariables(groupId, variables);
  }, [audienceActions]);

  const removeVariable = useCallback((variable: Variable) => {
    const groupId = latest.current.activeGroup;
    if (groupId !== null) audienceActions.removeVariable(groupId, variable.tag_id);
  }, [audienceActions]);

  const actions = useMemo<BuilderActions>(
    () => ({ setActiveGroup, toggleGroup, addGroup, removeGroup, selectVariables, removeVariable }),
    [toggleGroup, addGroup, removeGroup, selectVariables, removeVariable]
  );

  const state = useMemo<BuilderState>(() => {
    const activeVars = groups.find((g) => g.id === activeGroup)?.variables ?? [];
    return {
      activeGroup,
      selected: new Set(groups.flatMap((g) => tagIds(g.variables))),
      active: new Set(tagIds(activeVars)),
      disabled,
    };
  }, [activeGroup, disabled, groups]);

  return (
    <BuilderActionsContext.Provider value={actions}>
      <BuilderStateContext.Provider value={state}>
        {children}
      </BuilderStateContext.Provider>
    </BuilderActionsContext.Provider>
  );
};

/** The builder's UI state. Re-renders whenever it changes. */
export const useBuilder = () => {
  const state = useContext(BuilderStateContext);
  if (state === undefined) {
    throw new Error("useBuilder must be used within a BuilderProvider");
  }
  return state;
};

/** Actions for the builder. Never changes identity. */
export const useBuilderActions = () => {
  const actions = useContext(BuilderActionsContext);
  if (actions === undefined) {
    throw new Error("useBuilderActions must be used within a BuilderProvider");
  }
  return actions;
};
